import { useState } from 'react'
import { Main } from '@/components/layout/main'
import { Button } from '@/components/ui/button'
import { WelcomeVideoModal } from '@/components/onboarding'
import { StatCard, AgendaList, QuickTasks, MiniCalendar, RecentFilesTable } from '@/components/dashboard'
import { useAuthStore } from '@/stores/authStore'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { Gavel, FolderOpen, Calendar, ClipboardList, CalendarDays, PlayCircle } from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'
import { ar, fr } from 'date-fns/locale'

// Mock data - replace with real API calls
import { useQuery } from '@tanstack/react-query'
import { get_cases } from '@/api/case'
import { meetingService } from '@/api/calendar'
import { get_dashboard_stats } from '@/api/dashboard'
import { get_active_todos } from '@/api/todos'
import { timestamp_to_date } from '@/utils/time'
import { usePermission } from '@/hooks/use-permission'
import { UserPermission } from '@/constants/permissions'

const useDashboardData = () => {
    const { i18n } = useTranslation()
    const today = new Date()

    // Fetch Today's Agenda
    const { data: agendaData, isLoading: isLoadingAgenda } = useQuery({
        queryKey: ['dashboard-agenda', today.getDate(), today.getMonth(), today.getFullYear()],
        queryFn: async () => {
            const meetings = await meetingService.getMeetings(
                today.getMonth() + 1, // API expects 1-based month
                today.getFullYear(),
                today.getDate()
            )
            return meetings
        }
    })

    // Fetch Recent Files
    const { data: recentFilesData, isLoading: isLoadingFiles } = useQuery({
        queryKey: ['dashboard-recent-files'],
        queryFn: () => get_cases({
            page: 1,
            page_size: 5,
            ordering: '-created_at'
        })
    })

    // Fetch Dashboard Stats
    const { data: stats } = useQuery({
        queryKey: ['dashboard-stats'],
        queryFn: get_dashboard_stats
    })

    // Fetch Active Tasks Count
    const { data: activeTasks } = useQuery({
        queryKey: ['todos', 'active'],
        queryFn: get_active_todos
    })

    return {
        agenda: agendaData || [],
        isLoadingAgenda,
        recentFiles: recentFilesData?.results || [],
        isLoadingFiles,
        stats,
        activeTasksCount: activeTasks?.length || 0,
        locale: i18n.language === 'fr' ? fr : ar
    }
}





// Helper to map Meeting to AgendaItem
const mapMeetingToAgendaItem = (meeting: any, t: any) => {

    const startTime = new Date(meeting.start_time * 1000)
    const hours = startTime.getHours()
    const period = hours < 12 ? t('agenda_time_morning') : t('agenda_time_evening')
    const time = format(startTime, 'HH:mm')

    // Title: Type of meeting (desc) + Tribunal (if available) - Matches "جلسة مرافعة - المحكمة الابتدائية"
    const titleParts = [meeting.desc, meeting.tribunal].filter(Boolean)
    const title = titleParts.length > 0 ? titleParts.join(' - ') : t('appointment')

    // Description: Case Name - Matches "ملف رقم..."
    const description = meeting.case ? meeting.case.name : ''

    return {
        id: meeting.id,
        time,
        period,
        title,
        description,
        type: (meeting.tribunal_proc_type ? 'court' : 'office') as 'court' | 'office',
        client: meeting.client_name ? { name: meeting.client_name } : undefined
    }
}

// Helper to map Case to FileRow
const mapCaseToFileRow = (caseItem: any, locale: any) => {
    const { year, code, number, updated_at } = caseItem
    const formattedId = [year, code, number].filter(Boolean).join('/')
    // updated_at is a timestamp (number) because dashboard uses raw API data without Zod parsing
    const date = timestamp_to_date(updated_at)

    return {
        id: caseItem.id.toString(),
        refNumber: formattedId || `File-${caseItem.id}`,
        name: caseItem.name,
        client: {
            name: caseItem.client.name,
            initials: caseItem.client.name.charAt(0),
            color: 'bg-blue-100 text-blue-600' // Default color
        },
        status: caseItem.status || (caseItem.archived_at ? 'archived' : 'active'),
        lastUpdate: (
            <>
                <div className='w-fit text-nowrap'>
                    {date.toLocaleDateString('en-GB')}
                </div>
                <span className="w-fit text-nowrap text-xs text-muted-foreground">({formatDistanceToNow(date, { addSuffix: true, locale })})</span>
            </>
        )
    }
}


export default function DashboardHome() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [welcomeVideoOpen, setWelcomeVideoOpen] = useState(false)
    const user = useAuthStore((state) => state.auth.user)
    const { can } = usePermission()


    const { agenda, recentFiles, stats, activeTasksCount, locale } = useDashboardData()

    // Map data for display
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const agendaItems = agenda.map((item: any) => mapMeetingToAgendaItem(item, t))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recentFilesItems = recentFiles.map((item: any) => mapCaseToFileRow(item, locale))

    const today = new Date()
    const formattedDate = format(today, 'EEEE, d MMMM yyyy', { locale })

    const getGreeting = () => {
        const hour = today.getHours()
        if (hour < 12) return t('good_morning')
        if (hour < 18) return t('good_evening')
        return t('good_evening')
    }

    // Calculate Next Meeting
    const nextMeeting = agendaItems.find((item: any) => {
        // Parse "HH:mm" back to date or use original meeting object if passed differently
        // Since we map it, let's use the original agenda data sorted
        const meeting = agenda.find((m: any) => m.id === item.id)
        if (!meeting) return false
        const meetingTime = new Date(meeting.start_time * 1000)
        return meetingTime > new Date()
    })

    const nextMeetingText = nextMeeting
        ? `${t('next_appointment')}: ${nextMeeting.time} ${nextMeeting.period}`
        : `${t('no_upcoming_appointments')}`



    return (
        <Main className="p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-foreground mb-1">
                        {getGreeting()}، {user?.name?.split(' ')[0] || 'محمد'} 👋
                    </h2>
                    <p className="text-muted-foreground text-sm">
                        {t('dashboard_subtitle')}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setWelcomeVideoOpen(true)}
                        className="flex items-center gap-2 border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-all hover:scale-[1.02]"
                        title={t("onboarding.welcome_video.replay_tooltip", "مشاهدة الفيديو التعريفي الشامل للمنصة (90 ثانية)")}
                    >
                        <PlayCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                        <span>{t("onboarding.welcome_video.replay_button", "دليل المنصة (90 ثانية)")}</span>
                    </Button>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card px-3 py-1.5 rounded-lg shadow-sm border border-border">
                        <CalendarDays className="w-4 h-4 text-secondary" />
                        <span>{formattedDate}</span>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard
                    title={t('upcoming_sessions')}
                    value={stats?.upcoming_sessions.count || 0}
                    icon={Gavel}
                    trend={{
                        value: `+${stats?.upcoming_sessions.trend.value || 0} ${t('today_in_trend')}`,
                        type: stats?.upcoming_sessions.trend.direction || 'neutral'
                    }}
                    iconBgColor="bg-blue-100 dark:bg-blue-900/30"
                    iconColor="text-primary dark:text-blue-300"
                    onClick={() => navigate({ to: '/dashboard' })}
                />
                <StatCard
                    title={t('active_files')}
                    value={stats?.active_folders.count || 0}
                    icon={FolderOpen}
                    trend={{ value: `${stats?.active_folders.new_this_week || 0} ${t('new_files_this_week')}`, type: 'neutral' }}
                    iconBgColor="bg-yellow-100 dark:bg-yellow-900/30"
                    iconColor="text-secondary dark:text-yellow-300"
                    onClick={() => navigate({ to: '/cases' })}
                />
                {(can(UserPermission.VIEW_RENDEZVOUS) || can(UserPermission.VIEW_OWN_RENDEZVOUS)) && (
                    <StatCard
                        title={t('todays_appointments')}
                        value={agendaItems.length}
                        icon={Calendar}
                        trend={{ value: nextMeetingText, type: 'neutral' }}
                        iconBgColor="bg-purple-100 dark:bg-purple-900/30"
                        iconColor="text-purple-600 dark:text-purple-300"
                        onClick={() => navigate({ to: '/dashboard' })}
                    />
                )}
                {can(UserPermission.VIEW_TODO) && (
                    <StatCard
                        title={t('pending_tasks')}
                        value={activeTasksCount}
                        icon={ClipboardList}
                        trend={{ value: t('active_tasks_count_label'), type: 'neutral' }}
                        iconBgColor="bg-red-100 dark:bg-red-900/30"
                        iconColor="text-red-600 dark:text-red-300"
                    />
                )}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {(can(UserPermission.VIEW_RENDEZVOUS) || can(UserPermission.VIEW_OWN_RENDEZVOUS)) && (
                        <AgendaList
                            items={agendaItems}
                            title={t('todays_agenda')}
                            onViewAll={() => navigate({ to: '/dashboard' })}
                        />
                    )}

                    {(can(UserPermission.VIEW_CASE) || can(UserPermission.VIEW_OWN_CASE)) && (
                        <RecentFilesTable
                            files={recentFilesItems}
                            title={t('dashboard_recent_files')}
                            onViewAll={() => navigate({ to: '/cases' })}
                        />
                    )}
                </div>

                {/* Right Column - Sidebar Widgets */}
                <div className="space-y-6">
                    {can(UserPermission.VIEW_TODO) && <QuickTasks />}

                    {(can(UserPermission.VIEW_RENDEZVOUS) || can(UserPermission.VIEW_OWN_RENDEZVOUS)) && (
                        <MiniCalendar
                            onDateSelect={(date) => console.log('Selected date:', date)}
                            onViewCalendar={() => navigate({ to: '/dashboard' })}
                            upcomingCount={stats?.upcoming_sessions.upcoming_thisMonth}
                        />
                    )}
                </div>
            </div>

            <WelcomeVideoModal
                open={welcomeVideoOpen}
                onOpenChange={setWelcomeVideoOpen}
                autoTrigger={true}
            />
        </Main>
    )
}
